import { createContext, useContext } from "react";
import type {
  MaintenanceLevel,
  MaintenanceScope,
  ScheduledMaintenanceInfo,
  LimitedModule,
} from "@/types/maintenance-window";
import type { SaveStatus } from "@/modules/maintenance/hooks/useMaintenanceAutoSave";

export type GracePhase = "warn" | "save" | "readonly" | "logout" | null;

export type ConnectionMode = "websocket" | "polling" | "disconnected";

export interface MaintenanceState {
  isActive: boolean;
  level: MaintenanceLevel | null;
  scope: MaintenanceScope | null;
  message: string | null;
  estimatedEnd: string | null;
  affectedModules: string[];
  gracePeriodEndsAt: string | null;
  phase: GracePhase;
  connectionMode: ConnectionMode;
  isReadOnly: boolean;
  isEmergency: boolean;
  saveStatus: SaveStatus;
  scheduledMaintenance: ScheduledMaintenanceInfo | null;
  limitedModules: LimitedModule[];
  /** Contrato item 3 (28/09/2026): null sem token/token inválido (o back
   * ainda não respondeu ou não deu pra saber); true/false = tem ou não
   * maintenance:activate. Usado pelo ProtectedRoute pra decidir entre
   * banner de admin operando e redirecionar pra /maintenance — nunca
   * decide nada em cima de null (evita redirecionar em falso). */
  podeOperar: boolean | null;
}

export interface MaintenanceContextValue extends MaintenanceState {
  isModuleAffected: (moduleId: string) => boolean;
  registerDirtyForm: (formId: string, getData: () => Record<string, unknown> | null) => void;
  unregisterForm: (formId: string) => void;
  showReturnBanner: boolean;
  hasDraft: boolean;
  draftCount: number;
  draftRoute: string | null;
  draftTimestamp: string | null;
  restoreDraft: () => void;
  dismissBanner: () => void;
  dismissDraft: () => void;
  reopenBanner: () => void;
  dismissScheduledBanner: () => void;
}

export const MAINTENANCE_DEFAULT_STATE: MaintenanceState = {
  isActive: false,
  level: null,
  scope: null,
  message: null,
  estimatedEnd: null,
  affectedModules: [],
  gracePeriodEndsAt: null,
  phase: null,
  connectionMode: "disconnected",
  isReadOnly: false,
  isEmergency: false,
  saveStatus: "idle",
  scheduledMaintenance: null,
  limitedModules: [],
  podeOperar: null,
};

export const MaintenanceContext = createContext<MaintenanceContextValue | undefined>(undefined);

export const useMaintenanceContext = (): MaintenanceContextValue => {
  const context = useContext(MaintenanceContext);
  if (!context) {
    throw new Error("useMaintenanceContext must be used within a MaintenanceProvider");
  }
  return context;
};
